import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import {
  asyncScheduler,
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  observeOn,
  of,
  skip,
  startWith,
  Subject,
  Subscription,
  take,
  withLatestFrom,
} from 'rxjs';
import { select, Store } from '@ngrx/store';

import { getViewedNotifications, GlobalBookingFlight, LocationPair, NotificationActions } from '@fcom/common/store';
import {
  AppPreloadingStrategyService,
  AppState,
  compareAsJson,
  ConfigService,
  GlobalBookingTravelClass,
  isEmptyObjectOrHasEmptyValues,
  isNotEmpty,
  isPresent,
  RootPaths,
  unsubscribe,
  WindowRef,
} from '@fcom/core';
import { ButtonMode, ButtonSize, ButtonTheme, NotificationTheme, PopoverService } from '@fcom/ui-components';
import { GlobalBookingTripDates, MediaQueryService, PriceCalendarService, TripType } from '@fcom/common';
import { Amount, PaxAmount } from '@fcom/dapi';
import { LoginStatus, Profile } from '@fcom/core-api/login';
import { AirCalendarList } from '@fcom/dapi/api/models';
import { PriceCalendarType } from '@fcom/common/components/price-calendar/price-calendar.component';
import { locationPairIncludesTrainDestination } from '@fcom/common-booking/utils/common-booking.utils';
import { finShare } from '@fcom/rx';
import { LanguageService } from '@fcom/ui-translate';

import { BOOKING_WIDGET_CONTEXT } from '../../constants';
import {
  ExpanderStatus,
  LocationParam,
  LocationType,
  NotificationWarning,
  PaxUpdateEvent,
  SelectorType,
  WidgetLayout,
  WidgetTab,
  WidgetTheme,
} from '../../interfaces';
import { BookingWidgetService } from '../../services/booking-widget.service';
import { BookingWidgetFlightService } from '../../services/booking-widget-flight.service';
import { BookingWidgetTripService } from '../../services/booking-widget-trip.service';
import { TravelDatesSelectorComponent } from '../../components/travel-dates-selector/travel-dates-selector.component';
import { seasonalNotificationHandler } from '../../utils/utils';
import { BookingWidgetActions } from '../../store';

@Component({
  selector: 'fin-booking-widget-common',
  templateUrl: './booking-widget-common.component.html',
  styleUrls: ['./booking-widget-common.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BookingWidgetCommonComponent implements OnInit, OnDestroy, AfterViewInit {
  private bookingWidgetService = inject(BookingWidgetService);
  private bookingWidgetFlightService = inject(BookingWidgetFlightService);
  private bookingWidgetTripService = inject(BookingWidgetTripService);
  private windowRef = inject(WindowRef);
  private store$ = inject<Store<AppState>>(Store);
  private preloadStrategyService = inject(AppPreloadingStrategyService);
  private priceCalendarService = inject(PriceCalendarService);
  private configService = inject(ConfigService);
  private mediaQueryService = inject(MediaQueryService);
  private popoverService = inject(PopoverService);
  private languageService = inject(LanguageService);
  private platform = inject(PLATFORM_ID);

  readonly ButtonTheme = ButtonTheme;
  readonly ButtonSize = ButtonSize;
  readonly ButtonMode = ButtonMode;
  readonly TripType = TripType;
  readonly WidgetTab = WidgetTab;
  readonly LoginStatus = LoginStatus;
  readonly WidgetLayout = WidgetLayout;
  readonly WidgetTheme = WidgetTheme;
  readonly SelectorType = SelectorType;
  readonly LocationType = LocationType;
  readonly NotificationTheme = NotificationTheme;
  readonly NEW_MULTI_CITY_NOTIFICATION_ID = 87731561;
  readonly PriceCalendarType = PriceCalendarType;

  @Input()
  compactMode = false;

  @Input()
  defaultLocations$: Observable<LocationPair[]> = of([]);

  @Input()
  layout: WidgetLayout = WidgetLayout.DEFAULT;

  @Input()
  isGlobalBookingWidget = false;

  @Input()
  usePopoverSelectors = false;

  @Input()
  identifier: string;

  @Input()
  isFrontPage = false;

  @Input()
  enablePromoCode = true;

  @Output()
  changeGlobalBookingWidgetExpanderStatus = new EventEmitter<ExpanderStatus>();

  @ViewChild('footer', { static: false, read: ElementRef })
  footer: ElementRef<HTMLElement>;

  @ViewChild(TravelDatesSelectorComponent, { static: false })
  travelDatesSelector: TravelDatesSelectorComponent;

  tripType$: Observable<TripType>;
  travelClass$: Observable<GlobalBookingTravelClass>;
  availableTravelClasses$: Observable<GlobalBookingTravelClass[]>;
  paxAmount$: Observable<PaxAmount>;
  locations$: Observable<LocationPair>;
  flights$: Observable<GlobalBookingFlight[]>;
  travelDates$: Observable<GlobalBookingTripDates>;
  startingPrice$: Observable<Amount> = of(null);
  continueEnabled$: Observable<boolean>;
  airCalendarPrices$: Observable<AirCalendarList>;
  discountCode$: Observable<string>;
  profile$: Observable<Profile>;
  loginStatus$: Observable<LoginStatus>;
  loading$: Subject<boolean> = new Subject<boolean>();
  isNewSearchEnabled$ = of(false);
  showCompact$ = of(true);
  subscription = new Subscription();
  showDiscountCode$: Observable<boolean>;
  enableMultiCity: boolean;
  bookingFlowType$: Observable<WidgetTab> = of(WidgetTab.FLIGHT);
  shouldFocusWidgetContent$: Subject<void> = new Subject<void>();
  isNewMultiCityNotificationVisible$: Observable<boolean>;
  /**
   * @description When parameters changed and are valid you can enable the search again
   */
  shouldSearchBeEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showFlexibleDatesSelection$: Observable<boolean>;
  hideFooterPrice$: Observable<boolean>;
  showTripPriceFrom$: Observable<boolean>;
  private newSearch$: Subject<boolean> = new Subject<boolean>();

  ngOnInit(): void {
    this.subscription.add(
      combineLatest([
        this.bookingWidgetService.flightSearchParams$,
        this.bookingWidgetService.currentSearch$,
      ]).subscribe(([currentParams, lastSearch]) => {
        const areSameParams = compareAsJson(currentParams, lastSearch);
        if (!areSameParams) {
          const isValid = this.bookingWidgetService.validateFlightSearchParams(currentParams);
          this.shouldSearchBeEnabled$.next(isValid);
        } else {
          this.shouldSearchBeEnabled$.next(false);
        }
      })
    );
    this.bookingWidgetService.setCurrentSearchToCurrentParams();
    //Test change flight param and isValid=true
    this.enableMultiCity = this.configService.cfg.enableMultiCity;
    this.bookingWidgetService.setUsePopoverSelectors(this.usePopoverSelectors && this.isGlobalBookingWidget);

    // Selections
    this.paxAmount$ = this.bookingWidgetTripService.paxAmount$;
    this.tripType$ = this.bookingWidgetTripService.selectedTripType$;
    this.travelDates$ = this.bookingWidgetService.travelDates$;
    this.travelClass$ = this.bookingWidgetTripService.selectedTravelClass$;
    this.locations$ = this.bookingWidgetFlightService.locations$;
    this.flights$ = this.bookingWidgetFlightService.flights$;
    this.discountCode$ = this.bookingWidgetService.discountCode$;
    this.showCompact$ = this.bookingWidgetService.showCompact$.pipe(
      map((showCompact) => showCompact && this.compactMode)
    );
    // Utility
    this.profile$ = this.bookingWidgetService.profile$;
    this.availableTravelClasses$ = this.bookingWidgetTripService.availableTravelClasses$;
    this.loginStatus$ = this.bookingWidgetService.loginStatus$;

    this.continueEnabled$ = this.bookingWidgetService.continueEnabled$;
    this.bookingFlowType$ = this.bookingWidgetTripService.activeTab$;

    // Prices
    this.airCalendarPrices$ = this.bookingWidgetService.airCalendarPrices$;
    this.startingPrice$ = this.priceCalendarService.startingFromPrice$;
    //set default location
    this.bookingWidgetFlightService.setDefaultLocations(this.defaultLocations$);

    //reset the change status of booking widget
    this.bookingWidgetService.resetLocalOriginalBookingSelection();

    this.showDiscountCode$ = combineLatest([this.bookingFlowType$, this.tripType$]).pipe(
      map(([bookingFlowType, tripType]) => {
        if (!this.enablePromoCode) {
          return false;
        }

        if (bookingFlowType !== WidgetTab.FLIGHT) {
          return false;
        }

        if (!this.enableMultiCity) {
          return true;
        }

        return this.enableMultiCity && tripType !== TripType.MULTICITY;
      })
    );

    if (this.isGlobalBookingWidget) {
      this.bookingWidgetService.setLocalOriginalBookingSelection();
      this.bookingWidgetService.setPreviousSearchChange();
      //changed status of booking widget

      this.subscription.add(
        this.bookingWidgetService.notificationWarning$
          .pipe(
            map((notification) =>
              Object.keys(notification)
                .filter((notificationType) => notificationType !== 'seasonalRoute')
                .reduce((filteredNotification, key) => {
                  filteredNotification[key] = notification[key];
                  return filteredNotification;
                }, {})
            ),
            filter((notification) => !isEmptyObjectOrHasEmptyValues(notification))
          )
          .subscribe(() => {
            this.changeGlobalBookingWidgetExpanderStatus.emit(ExpanderStatus.OPEN);
          })
      );
    }

    this.isNewMultiCityNotificationVisible$ = this.store$.pipe(
      select(getViewedNotifications),
      map((viewedNotifications) => {
        return !viewedNotifications.includes(this.NEW_MULTI_CITY_NOTIFICATION_ID);
      })
    );

    this.subscription.add(
      // on client interaction with the widget (2nd emission of booking selections), trigger preloading booking module
      this.bookingWidgetService.bookingSelections$
        .pipe(skip(1), take(1))
        .subscribe(() => this.preloadStrategyService.triggerPreload(RootPaths.BOOKING_ROOT))
    );

    this.showFlexibleDatesSelection$ = combineLatest([this.bookingFlowType$, this.locations$]).pipe(
      map(([bookingFlowType, locations]) => {
        if (
          this.layout === WidgetLayout.HORIZONTAL ||
          bookingFlowType === WidgetTab.AWARD ||
          locationPairIncludesTrainDestination(locations)
        ) {
          this.bookingWidgetService.setFlexibleDates(false);
          return false;
        }

        return true;
      }),
      finShare()
    );

    const noPrices$ = combineLatest([
      this.priceCalendarService.pricesAvailable$,
      this.priceCalendarService.pricesAvailableForSelectedDates$,
    ]).pipe(
      map(([pricesAvailable, pricesAvailableForSelectedDates]) => !pricesAvailable || !pricesAvailableForSelectedDates),
      startWith(false)
    );

    this.hideFooterPrice$ = combineLatest([this.bookingFlowType$, this.locations$, this.tripType$, noPrices$]).pipe(
      map(([bookingFlowType, locations, tripType, noPrices]) => {
        const isHorizontalLayout = this.layout === WidgetLayout.HORIZONTAL;
        const isMulticity = tripType === TripType.MULTICITY;
        const isAwardFlow = bookingFlowType === WidgetTab.AWARD;
        const isTrainDestination = locationPairIncludesTrainDestination(locations);
        return isMulticity || isAwardFlow || isTrainDestination || isHorizontalLayout || noPrices;
      }),
      finShare()
    );

    this.showTripPriceFrom$ = this.locations$.pipe(
      map((locations) => {
        return !locationPairIncludesTrainDestination(locations);
      }),
      finShare()
    );

    this.subscription.add(
      combineLatest([
        this.tripType$,
        this.travelClass$,
        this.locations$,
        this.travelDates$,
        this.paxAmount$,
        this.languageService.lang,
      ])
        .pipe(
          filter(([tripType]) => tripType !== TripType.MULTICITY),
          map(([tripType, travelClass, { origin, destination }, travelDates, paxAmount, locale]) => ({
            origin: origin?.locationCode,
            destination: destination?.locationCode,
            tripType,
            travelClass,
            initialTravelDates: travelDates,
            paxAmount,
            locale,
          })),
          distinctUntilChanged(
            (prev, next) =>
              prev.origin === next.origin &&
              prev.destination === next.destination &&
              prev.tripType === next.tripType &&
              prev.travelClass === next.travelClass &&
              prev.initialTravelDates === next.initialTravelDates &&
              prev.locale === next.locale
          )
        )
        .subscribe((params) => this.priceCalendarService.updateParams(params))
    );

    this.subscription.add(
      this.newSearch$
        .pipe(
          withLatestFrom(this.mediaQueryService.isBreakpoint$('tablet_down')),
          filter(([_, isMobile]) => isMobile)
        )
        .subscribe(() => {
          this.changeGlobalBookingWidgetExpanderStatus.emit(ExpanderStatus.CLOSE);
        })
    );

    this.subscription.add(
      this.priceCalendarService.availability$
        .pipe(
          filter((availability) => isNotEmpty(availability) && availability.length > 0),
          withLatestFrom(this.tripType$, this.languageService.translate<Record<string, string | string[]>>('date')),
          filter(([, tripType]) => tripType !== TripType.MULTICITY)
        )
        .subscribe(([availability, , dateTranslations]) => {
          const seasonalNotificationData = seasonalNotificationHandler(availability, dateTranslations);
          this.store$.dispatch(
            BookingWidgetActions.setNotificationWarning({
              key: NotificationWarning.SEASONAL_ROUTE,
              isActive: true,
              data: seasonalNotificationData,
            })
          );
        })
    );
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platform) && !this.isGlobalBookingWidget) {
      this.subscription.add(
        this.shouldFocusWidgetContent$
          .pipe(
            withLatestFrom(this.showCompact$),
            filter(([, showCompact]) => !showCompact),
            observeOn(asyncScheduler),
            filter(() => this.footerIsNotInView())
          )
          .subscribe(() => {
            this.footer.nativeElement.scrollIntoView({
              block: 'end',
              inline: 'nearest',
              behavior: 'smooth',
            });
          })
      );
    }
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
    if (this.isGlobalBookingWidget) {
      this.changeGlobalBookingWidgetExpanderStatus.emit(ExpanderStatus.CLOSE);
    }
  }

  setTripType(tripType: TripType): void {
    if (this.isGlobalBookingWidget) {
      this.bookingWidgetService.setSelectionChangeType(SelectorType.TRIP_TYPE);
    }
    this.bookingWidgetTripService.setTripType(tripType);
  }

  openCalendar(): void {
    if (this.travelDatesSelector) {
      this.travelDatesSelector.openModal();
    }
  }

  setPaxAmount({ paxType, amount, updateType }: PaxUpdateEvent): void {
    if (this.isGlobalBookingWidget) {
      this.bookingWidgetService.setSelectionChangeType(SelectorType.PAX);
    }
    this.bookingWidgetTripService.setPaxAmount({ paxType, amount, updateType });
  }

  setLocations({ locations, locationType }: LocationParam, index = 0): void {
    if (this.isGlobalBookingWidget) {
      locationType.forEach((type) => {
        this.bookingWidgetService.setSelectionChangeType(type as unknown as SelectorType);
      });
    }
    this.bookingWidgetFlightService.setLocations(locations, index);
    this.shouldFocusWidgetContent$.next();
  }

  setTravelDates(dates: GlobalBookingTripDates, isAirCalendar = false): void {
    if (this.isGlobalBookingWidget) {
      this.bookingWidgetService.setSelectionChangeType(SelectorType.TRAVEL_DATES);
    }
    this.bookingWidgetService.setTravelDates(dates, 0, isAirCalendar, this.isGlobalBookingWidget);
  }

  setFlexibleDates(isFlexibleDates: boolean): void {
    this.bookingWidgetService.setFlexibleDates(isFlexibleDates);
  }

  onAddReturnClick(): void {
    if (this.isGlobalBookingWidget) {
      this.bookingWidgetService.setSelectionChangeType(SelectorType.TRIP_TYPE);
    }
    this.bookingWidgetTripService.setReturnTripType();
  }

  cancelTravelDatesSelection(): void {
    this.subscription.add(
      this.bookingWidgetService.currentSearch$.pipe(take(1)).subscribe((originalSelection) => {
        this.bookingWidgetService.setTravelDates(
          {
            departureDate: originalSelection.flights[0]?.departureDate,
            returnDate: originalSelection.flights[1]?.departureDate,
          },
          0
        );

        this.bookingWidgetService.setTripType(originalSelection.tripType);
        this.bookingWidgetService.resetSearchSelection();
      })
    );
  }

  startNewSearch(): void {
    if (this.isGlobalBookingWidget) {
      this.shouldSearchBeEnabled$.next(false);
    }
    this.bookingWidgetService.startNewSearch(this.isGlobalBookingWidget);
    this.popoverService.closeByContext(BOOKING_WIDGET_CONTEXT);
    this.newSearch$.next(true);
  }

  navigateToBookingFlow(fromMatrix = false, airCalendarPrice: string | undefined = undefined): void {
    this.subscription.add(
      combineLatest([this.bookingWidgetService.isMultiCity$, this.flights$, this.tripType$])
        .pipe(take(1))
        .subscribe(([isMultiCity, flights, tripType]) => {
          if (isMultiCity) {
            this.bookingWidgetService.navigateToMultiCityBookingFlow(this.loading$);
            return;
          }
          //check if there are only two flights and the 1st origin as same as 2nd destination, then set triptype to return
          if (
            tripType !== TripType.RETURN &&
            flights.length === 2 &&
            flights[0].origin.locationCode === flights[1].destination.locationCode &&
            flights[0].destination.locationCode === flights[1].origin.locationCode
          ) {
            this.bookingWidgetTripService.setTripType(TripType.RETURN);
            this.bookingWidgetService.setTravelDates(
              { departureDate: flights[0].departureDate, returnDate: flights[1].departureDate },
              0
            );
          }
          this.bookingWidgetService.navigateToBookingFlow(fromMatrix, airCalendarPrice, this.loading$);
        })
    );
  }

  startSearch(): void {
    this.isGlobalBookingWidget ? this.startNewSearch() : this.navigateToBookingFlow();
  }

  private footerIsNotInView(): boolean {
    if (!isPresent(this.footer?.nativeElement)) {
      return false;
    }

    const footerRect = this.footer.nativeElement.getBoundingClientRect();
    const screenBottom = this.windowRef.nativeWindow.innerHeight;
    return footerRect.y + footerRect.height > screenBottom;
  }

  hideNewMultiCityNotification(): void {
    this.store$.dispatch(
      NotificationActions.setViewedNotification({
        id: this.NEW_MULTI_CITY_NOTIFICATION_ID,
      })
    );
  }
}
